import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldAlert } from "lucide-react";
import { changePasswordApi, AuthApiError } from "@/services/auth.service";
import { useAuthStore } from "@/store/auth.store";
import { PasswordField } from "@/components/auth/PasswordField";
import type { UserRole } from "@/types";
import { PasswordStrengthChecker } from "@/modules/auth/ui/components/PasswordStrengthChecker";
import { STORAGE_KEYS } from "@/lib/storage-keys";

const ROLE_DESTINATIONS: Record<UserRole, string> = {
  admin: "/admin/dashboard",
  teacher: "/teacher/dashboard",
  student: "/student/dashboard",
  family: "/parent/dashboard",
  guest: "/login",
};

export function ChangePasswordForm() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const role = useAuthStore((s) => s.role);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [isPasswordStrong, setIsPasswordStrong] = useState(false);

  const validate = (): boolean => {
    const errors: Record<string, string[]> = {};

    if (newPassword && !isPasswordStrong) {
      errors.password = [
        "Le nouveau mot de passe doit respecter toutes les exigences de sécurité.",
      ];
    }

    if (newPassword !== confirmPassword) {
      errors.password_confirmation = ["Les mots de passe ne correspondent pas."];
    }

    if (newPassword && newPassword === currentPassword) {
      errors.password = ["Le nouveau mot de passe doit être différent de l'ancien."];
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validate()) return;
    if (!token) {
      navigate("/login");
      return;
    }

    setIsLoading(true);

    try {
      await changePasswordApi(token, {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });

      localStorage.removeItem(STORAGE_KEYS.MUST_CHANGE_PASSWORD);
      navigate(ROLE_DESTINATIONS[role]);
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.status === 422 && Object.keys(err.fieldErrors).length > 0) {
          setFieldErrors(err.fieldErrors);
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : "Erreur inconnue");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Info banner */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
        <ShieldAlert size={20} className="text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-bold text-amber-800">Changement de mot de passe obligatoire</p>
          <p className="text-xs text-amber-600 mt-1">
            Pour des raisons de sécurité, vous devez modifier votre mot de passe temporaire avant
            d'accéder à votre espace.
          </p>
        </div>
      </div>

      <PasswordField
        label="Mot de passe actuel"
        value={currentPassword}
        onChange={setCurrentPassword}
        placeholder="Votre mot de passe temporaire"
        disabled={isLoading}
        errors={fieldErrors.current_password}
      />

      <PasswordField
        label="Nouveau mot de passe"
        value={newPassword}
        onChange={setNewPassword}
        placeholder="Minimum 8 caractères"
        disabled={isLoading}
        errors={fieldErrors.password}
      />

      {newPassword && (
        <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100">
          <PasswordStrengthChecker password={newPassword} onValidityChange={setIsPasswordStrong} />
        </div>
      )}

      <PasswordField
        label="Confirmer le nouveau mot de passe"
        value={confirmPassword}
        onChange={setConfirmPassword}
        placeholder="Retapez le nouveau mot de passe"
        disabled={isLoading}
        errors={fieldErrors.password_confirmation}
      />

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm font-bold text-red-600">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-[#003366] text-white rounded-xl py-5 font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-blue-900/20 hover:bg-black transition-all active:scale-[0.98] flex items-center justify-center gap-3 group disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
      >
        {isLoading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Mise à jour en cours…
          </>
        ) : (
          <>
            Changer mon mot de passe
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
          </>
        )}
      </button>
    </form>
  );
}
